import { httpsCallable } from 'firebase/functions';
import { useEffect, useRef, useState } from 'react';

import {
  isLevel5Atc,
  splitQueryTokens,
} from '@/components/search/suggestion-rows';
import type { DrugSuggestionGroup } from '@/hooks/use-drug-suggestions';
import { fns } from '@/integrations/firebase/functions';

type SuggestResponse = {
  groups: DrugSuggestionGroup[];
};

// Recover the human, Latin-substance display label for a committed query.
//
// The URL is the ATC code (the cache-convergence key), so a shared / refreshed
// link carries no human label — ``stateLabel`` (the typeahead selection) is only
// present on a warm, in-app navigation. On a cold load we look each ATC token up
// in the RPL registry (reusing the ``drug_suggest`` callable) and reuse the same
// Latin ``substance`` label the dropdown shows, turning e.g. ``N02BE01`` ->
// "Paracetamolum" and ``J01CA04+J01CR02`` -> "Amoksycylina + Kwas klawulanowy",
// so the search box never shows a bare code and always matches the picked row.
//
// Returns "" until an ATC lookup resolves (the caller keeps the box blank rather
// than flashing the code); a warm ``stateLabel`` and pure-name queries resolve
// synchronously. Per-code results are cached for the hook's lifetime.
export function useDrugLabel(
  query: string,
  stateLabel: string | null,
  enabled = true,
): string {
  const trimmed = (query ?? '').trim();
  const tokens = trimmed ? splitQueryTokens(trimmed) : [];
  // Only an ATC-coded query with no caller-provided label needs the async RPL
  // lookup; every other case is derived synchronously below, no state at all.
  const lookupKey =
    enabled && !stateLabel && tokens.some((token) => isLevel5Atc(token))
      ? trimmed
      : '';

  const [resolvedLabel, setResolvedLabel] = useState('');
  const cacheRef = useRef<Map<string, string>>(new Map());
  const requestIdRef = useRef(0);
  const callableRef = useRef(
    httpsCallable<{ q: string; limit?: number }, SuggestResponse>(
      fns,
      'drug_suggest',
    ),
  );

  // Reset the resolved label when the lookup target changes — the derived-state
  // pattern (setState during render), never a synchronous setState in an effect.
  const [trackedKey, setTrackedKey] = useState(lookupKey);
  if (lookupKey !== trackedKey) {
    setTrackedKey(lookupKey);
    setResolvedLabel('');
  }

  useEffect(() => {
    if (!lookupKey) return;

    const tokens = splitQueryTokens(lookupKey);
    const requestId = ++requestIdRef.current;
    let cancelled = false;

    void (async () => {
      const parts = await Promise.all(
        tokens.map(async (token) => {
          if (!isLevel5Atc(token)) return token;
          const code = token.toUpperCase();
          const cached = cacheRef.current.get(code);
          if (cached) return cached;
          try {
            const res = await callableRef.current({ q: code, limit: 8 });
            const groups = res.data.groups ?? [];
            const match = groups.find(
              (g) =>
                g.atc?.toUpperCase() === code ||
                g.atc_codes?.some((atc) => atc.toUpperCase() === code),
            );
            const name = match?.substance?.trim();
            if (name) {
              cacheRef.current.set(code, name);
              return name;
            }
          } catch {
            // Fall through to the raw code — a missing label must never break the
            // page; the search itself still runs on the ATC.
          }
          return token;
        }),
      );
      if (cancelled || requestId !== requestIdRef.current) return;
      setResolvedLabel(parts.join(' + '));
    })();

    return () => {
      cancelled = true;
    };
  }, [lookupKey]);

  // A caller-provided label (the exact string the user picked in the typeahead)
  // always wins — never override a deliberate selection with a lookup.
  if (stateLabel) return stateLabel;
  if (!enabled || !trimmed) return '';
  // No ATC codes → already human-readable (a substance name / free text).
  if (!lookupKey) return trimmed;
  // "" until the lookup resolves — the caller keeps the box blank rather than
  // flashing the code.
  return resolvedLabel;
}
